import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLesson } from '../../hooks/useLesson';
import { experimentService } from '../../services/experimentService';
import { Book, PlayCircle, CheckCircle, Beaker, Clock, ArrowLeft, AlertCircle } from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import Navbar from '../shared/Navbar';

const LessonViewer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { lesson, resources, experiment, loading, error, startLesson, completeLesson } = useLesson(id);

    const [showExperiment, setShowExperiment] = useState(false);
    const [experimentAnswers, setExperimentAnswers] = useState({});
    const [experimentResult, setExperimentResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [startTime] = useState(Date.now());

    useEffect(() => {
        if (lesson) {
            startLesson();
        }
    }, [lesson]);

    const handleAnswerSelect = (questionId, answer) => {
        setExperimentAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));
    };

    const handleSubmitExperiment = async () => {
        if (!experiment) return;

        try {
            setSubmitting(true);
            const timeTaken = Math.floor((Date.now() - startTime) / 1000);

            const result = await experimentService.submitExperiment(
                experiment.id,
                experimentAnswers,
                timeTaken
            );

            setExperimentResult(result);

            if (result.passed) {
                // Update lesson progress
                await completeLesson(Math.floor(timeTaken / 60));
            }
        } catch (err) {
            alert(err.message || 'Failed to submit experiment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleContinue = () => {
        if (experimentResult?.passed) {
            navigate('/student/dashboard');
        } else {
            // Retry experiment
            setExperimentAnswers({});
            setExperimentResult(null);
        }
    };

    if (loading) return <LoadingSpinner message="Loading lesson..." />;

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertCircle className="w-6 h-6 text-red-600" />
                            <h2 className="text-xl font-bold text-red-900">Error Loading Lesson</h2>
                        </div>
                        <p className="text-red-800 mb-4">{error}</p>
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="btn-primary"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!lesson) return null;

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/student/dashboard')}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Dashboard
                </button>

                {/* Lesson Header */}
                <div className="card mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-3xl font-bold text-gray-900">{lesson.title}</h1>
                        {lesson.progress_status === 'completed' && (
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        )}
                    </div>
                    <p className="text-gray-600 mb-4">{lesson.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
                {lesson.duration_minutes} minutes
            </span>
                        <span>•</span>
                        <span>{lesson.term_name}</span>
                    </div>
                </div>

                {!showExperiment ? (
                    <>
                        {/* Video Player */}
                        {lesson.video_url && (
                            <div className="card mb-6">
                                <h2 className="text-xl font-bold mb-4">Lesson Video</h2>
                                <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg aspect-video flex items-center justify-center">
                                    <div className="text-center text-white">
                                        <PlayCircle className="w-16 h-16 mx-auto mb-4" />
                                        <p className="text-lg font-semibold">Video Player</p>
                                        <p className="text-sm opacity-80 mt-2">
                                            Integrate your video player here (YouTube, Vimeo, etc.)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lesson Content */}
                        {lesson.content_html && (
                            <div className="card mb-6">
                                <h2 className="text-xl font-bold mb-4">Lesson Content</h2>
                                <div
                                    className="prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: lesson.content_html }}
                                />
                            </div>
                        )}

                        {/* Resources */}
                        {resources.length > 0 && (
                            <div className="card mb-6">
                                <h2 className="text-xl font-bold mb-4">📚 Learning Resources</h2>
                                <div className="space-y-3">
                                    {resources.map((resource) => (
                                        <a
                                            key={resource.id}
                                            href={resource.resource_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-purple-50 transition-all"
                                        >
                                            <Book className="w-5 h-5 text-purple-600" />
                                            <div>
                                                <p className="font-semibold">{resource.title}</p>
                                                {resource.description && (
                                                    <p className="text-sm text-gray-600">{resource.description}</p>
                                                )}
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Start Experiment */}
                        {experiment && (
                            <div className="card bg-gradient-to-r from-green-400 to-emerald-500 text-white">
                                <div className="flex items-center gap-4">
                                    <Beaker className="w-12 h-12 flex-shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold mb-1">Ready for the Experiment?</h3>
                                        <p>Complete the experiment to finish this lesson!</p>
                                    </div>
                                    <button
                                        onClick={() => setShowExperiment(true)}
                                        className="bg-white text-green-600 px-6 py-3 rounded-full font-bold hover:bg-green-50 transition-all"
                                    >
                                        Start Experiment
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    /* Experiment View */
                    <div className="card">
                        <h2 className="text-2xl font-bold text-purple-900 mb-4">
                            🧪 {experiment.title}
                        </h2>
                        <p className="text-gray-600 mb-6">{experiment.description}</p>

                        {!experimentResult ? (
                            <>
                                {/* Questions */}
                                <div className="space-y-6 mb-6">
                                    {experiment.questions?.map((question, idx) => (
                                        <div key={question.id} className="bg-purple-50 rounded-lg p-5">
                                            <p className="font-semibold text-lg mb-3">
                                                {idx + 1}. {question.question_text}
                                            </p>

                                            {question.question_type === 'multiple_choice' && (
                                                <div className="space-y-2">
                                                    {JSON.parse(question.options).map((option, optIdx) => (
                                                        <button
                                                            key={optIdx}
                                                            onClick={() => handleAnswerSelect(question.id, option)}
                                                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                                                experimentAnswers[question.id] === option
                                                                    ? 'border-purple-500 bg-purple-100'
                                                                    : 'border-gray-200 bg-white hover:border-purple-300'
                                                            }`}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {question.question_type === 'true_false' && (
                                                <div className="flex gap-4">
                                                    {['True', 'False'].map((option) => (
                                                        <button
                                                            key={option}
                                                            onClick={() => handleAnswerSelect(question.id, option)}
                                                            className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                                                experimentAnswers[question.id] === option
                                                                    ? 'border-purple-500 bg-purple-100'
                                                                    : 'border-gray-200 bg-white hover:border-purple-300'
                                                            }`}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubmitExperiment}
                                    disabled={
                                        submitting ||
                                        Object.keys(experimentAnswers).length < experiment.questions?.length
                                    }
                                    className="btn-primary w-full"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Experiment'}
                                </button>

                                <p className="text-sm text-gray-500 text-center mt-4">
                                    Passing score: {experiment.passing_score}% •
                                    Attempts remaining: {experiment.max_attempts}
                                </p>
                            </>
                        ) : (
                            /* Results */
                            <div className={`rounded-lg p-8 text-center ${
                                experimentResult.passed ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                                <div className="text-6xl mb-4">
                                    {experimentResult.passed ? '🎉' : '😔'}
                                </div>
                                <h3 className={`text-3xl font-bold mb-2 ${
                                    experimentResult.passed ? 'text-green-900' : 'text-red-900'
                                }`}>
                                    {experimentResult.passed ? 'Congratulations!' : 'Keep Trying!'}
                                </h3>
                                <p className="text-xl mb-4">
                                    You scored {experimentResult.score}%
                                </p>
                                <p className="text-lg mb-6">
                                    {experimentResult.earnedPoints} out of {experimentResult.totalPoints} points
                                </p>

                                {experimentResult.passed ? (
                                    <div>
                                        <p className="text-green-800 mb-4 text-lg">
                                            ✨ Great job! You've completed this lesson!
                                        </p>
                                        <button
                                            onClick={handleContinue}
                                            className="bg-green-600 text-white px-8 py-3 rounded-full font-bold hover:bg-green-700"
                                        >
                                            Back to Dashboard
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <p className="text-red-800 mb-4">
                                            {experimentResult.attemptsRemaining > 0
                                                ? `You have ${experimentResult.attemptsRemaining} attempts remaining`
                                                : 'No more attempts available'}
                                        </p>
                                        {experimentResult.attemptsRemaining > 0 ? (
                                            <button
                                                onClick={handleContinue}
                                                className="bg-red-600 text-white px-8 py-3 rounded-full font-bold hover:bg-red-700"
                                            >
                                                Try Again
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => navigate('/student/dashboard')}
                                                className="bg-gray-600 text-white px-8 py-3 rounded-full font-bold hover:bg-gray-700"
                                            >
                                                Back to Dashboard
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LessonViewer;